import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TeamApi } from './api';
import { ChatbotApi } from '@/lib/sdk/chatbot/api';
import type { 
  CreateTeamMemberDto,
  UpdateTeamMemberDto,
  TeamFilters,
} from './types';

const QUERY_KEYS = {
  all: ['team'] as const,
  list: (filters: TeamFilters) => [...QUERY_KEYS.all, 'list', filters] as const,
  detail: (id: string) => [...QUERY_KEYS.all, 'detail', id] as const,
};

export const useTeamMembers = (filters: TeamFilters) => {
  return useQuery({
    queryKey: QUERY_KEYS.list(filters),
    queryFn: () => TeamApi.getTeamMembers(filters),
    enabled: !!filters.org_id,
  });
};

export const useCreateTeamMembers = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTeamMemberDto) => TeamApi.createTeamMembers(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useUpdateTeamMembers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTeamMemberDto) => TeamApi.updateTeamMembers(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

/**
 * Replaces one member's reminder subscriptions in a single call. Invalidates the
 * whole `team` key because every member row carries a `calendar` array, which is
 * what the nudges page derives its counts and checkbox state from.
 */
export const useSetMemberNudges = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, nudgeIds }: { id: string; nudgeIds: number[] }) =>
      TeamApi.setMemberNudges(id, nudgeIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useUpdateTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      id, 
      data 
    }: { 
      id: string; 
      data: UpdateTeamMemberDto;
    }) => TeamApi.updateTeamMember(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.detail(id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useDeleteTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => TeamApi.deleteTeamMember(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  })
};

export const useDeleteChatbotTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) => ChatbotApi.deleteUser(id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  })
};